import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { Shield, Megaphone, LayoutGrid } from "lucide-react";
import { AnnouncementComposer } from "./AnnouncementComposer";
import { DARK, LIGHT } from "../constants/theme";

// AdminContext exposes whether the current user is an admin, and a mutable
// "admin mode" flag that toggles inline admin affordances (3-dot menus, user
// panels, etc.) across the app. Hidden by default so demos/screenshots are
// clean; toggle with Cmd/Ctrl+Shift+A.

const Ctx = createContext({
  isAdmin: false,
  adminMode: false,
  toggleAdminMode: () => {},
  setIsAdmin: () => {},
  openUser: null,
  setOpenUser: () => {},
});

const STORAGE_KEY = "bam_admin_mode";

export function AdminProvider({ children }) {
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminMode, setAdminMode] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === "1"; } catch { return false; }
  });
  // Shared state for the "jump-to-user" side panel so any component can open it.
  const [openUser, setOpenUser] = useState(null);

  const toggleAdminMode = useCallback(() => {
    setAdminMode(prev => {
      const next = !prev;
      try { localStorage.setItem(STORAGE_KEY, next ? "1" : "0"); } catch {}
      return next;
    });
  }, []);

  // Auto-turn-off if the user isn't actually an admin (defense in depth -
  // server still rejects, but this hides stale UI).
  useEffect(() => {
    if (!isAdmin && adminMode) setAdminMode(false);
  }, [isAdmin, adminMode]);

  // Hotkey: Option/Alt + Shift + A. (Cmd+Shift+A is Chrome's "Search tabs".)
  // Uses e.code so Mac's Option layer mapping - ⌥A → "å" - doesn't break it.
  useEffect(() => {
    if (!isAdmin) return;
    const onKey = (e) => {
      if (e.altKey && e.shiftKey && e.code === "KeyA") {
        e.preventDefault();
        toggleAdminMode();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isAdmin, toggleAdminMode]);

  return (
    <Ctx.Provider value={{ isAdmin, adminMode, toggleAdminMode, setIsAdmin, openUser, setOpenUser }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAdmin() {
  return useContext(Ctx);
}

// Floating admin toggle - always visible when you're an admin. Subtle when
// off, gold pill when on. Clicking toggles admin mode; keyboard shortcut
// is Option/Alt + Shift + A. When in admin mode, a small secondary button
// next to it opens the announcement composer.
export function AdminModeBadge() {
  const { isAdmin, adminMode, toggleAdminMode } = useAdmin();
  const [composerOpen, setComposerOpen] = useState(false);
  let dark = true;
  try { dark = (localStorage.getItem("bam_dark_mode") ?? "1") === "1"; } catch {}
  const C = dark ? DARK : LIGHT;
  if (!isAdmin) return null;
  if (adminMode) {
    return (
      <>
        <div className="bam-admin-float" style={{position:"fixed",bottom:20,right:84,zIndex:5000,display:"flex",alignItems:"center",gap:8}}>
          <button
            onClick={()=>{window.location.hash="#/admin/home";}}
            title="Admin Home"
            style={{
              width:30,height:30,borderRadius:15,
              background:"rgba(10,10,10,0.85)",border:"1px solid rgba(226,221,159,0.4)",
              backdropFilter:"blur(6px)",color:"#E2DD9F",cursor:"pointer",
              display:"flex",alignItems:"center",justifyContent:"center",
            }}
          >
            <LayoutGrid size={13}/>
          </button>
          <button
            onClick={()=>setComposerOpen(true)}
            title="Post an announcement"
            style={{
              width:30,height:30,borderRadius:15,
              background:"rgba(10,10,10,0.85)",border:"1px solid rgba(226,221,159,0.4)",
              backdropFilter:"blur(6px)",color:"#E2DD9F",cursor:"pointer",
              display:"flex",alignItems:"center",justifyContent:"center",
            }}
          >
            <Megaphone size={13}/>
          </button>
          <div
            onClick={toggleAdminMode}
            title="Admin mode on - click or press ⌥⇧A to hide"
            style={{
              padding: "7px 12px", borderRadius: 20,
              background: "#E2DD9F", color: "#111",
              fontSize: 11, fontWeight: 800, letterSpacing: 1, textTransform: "uppercase",
              cursor: "pointer",
              boxShadow: "0 6px 20px rgba(226,221,159,0.45), 0 2px 6px rgba(0,0,0,0.25)",
              display: "flex", alignItems: "center", gap: 8,
              userSelect: "none", height: 30,
            }}
          >
            <span style={{width:7,height:7,borderRadius:"50%",background:"#111",boxShadow:"0 0 8px rgba(0,0,0,0.3)",animation:"bamAdminPulse 1.4s ease-in-out infinite"}}/>
            Admin mode
          </div>
        </div>
        {composerOpen && (
          <AnnouncementComposer C={C} dark={dark}
            onClose={()=>setComposerOpen(false)}
            onPosted={()=>setComposerOpen(false)}/>
        )}
      </>
    );
  }
  // Off state - subtle outline button for admins only.
  return (
    <div
      className="bam-admin-float"
      onClick={toggleAdminMode}
      title="Turn on admin mode (⌥⇧A)"
      style={{
        // Beside the bottom-right floater; moves over when BAM IQ's panel pushes the page.
        position: "fixed", bottom: 29, right: "calc(84px + var(--bamiq-push, 0px))", zIndex: 5000,
        width: 34, height: 34, borderRadius: 17,
        background: "rgba(0,0,0,0.55)",
        border: "1px solid rgba(226,221,159,0.35)",
        backdropFilter: "blur(6px)",
        color: "#E2DD9F",
        cursor: "pointer",
        display: "flex", alignItems: "center", justifyContent: "center",
        boxShadow: "0 4px 14px rgba(0,0,0,0.35)",
        userSelect: "none",
        opacity: 0.8,
        transition: "opacity .15s ease, transform .15s ease",
      }}
      onMouseEnter={(e)=>{e.currentTarget.style.opacity="1";e.currentTarget.style.transform="scale(1.05)";}}
      onMouseLeave={(e)=>{e.currentTarget.style.opacity="0.8";e.currentTarget.style.transform="scale(1)";}}
    >
      <Shield size={14} color="#E2DD9F"/>
    </div>
  );
}
